import { clearAuth, getAccessToken, setAccessToken, setUserRole } from './auth';

const apiRoot = process.env.REACT_APP_API_URL?.replace(/\/$/, '') ?? '/api';
const authBase = apiRoot
  ? apiRoot.endsWith('/api/auth')
    ? apiRoot
    : apiRoot.endsWith('/api')
      ? `${apiRoot}/auth`
      : `${apiRoot}/api/auth`
  : '/api/auth';

export interface AuthResponse {
  accessToken: string;
  expiresInMinutes: number;
  role: string;
}

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
    this.name = 'ApiError';
  }
}

const handleJsonResponse = async (response: Response) => {
  if (!response.ok) {
    throw new ApiError(response.status, `Request failed: ${response.status}`);
  }

  return response.json();
};

export const login = async (email: string, password: string): Promise<AuthResponse> => {
  const response = await fetch(`${authBase}/login`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({ email, password }),
  });

  const result = (await handleJsonResponse(response)) as AuthResponse;
  setAccessToken(result.accessToken);
  setUserRole(result.role);
  return result;
};

export const refreshSession = async (): Promise<AuthResponse> => {
  const response = await fetch(`${authBase}/refresh`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });
  if (response.status === 401) {
    clearAuth();
    throw new ApiError(response.status, 'Refresh token invalid or expired.');
  }
  const result = (await handleJsonResponse(response)) as AuthResponse;
  setAccessToken(result.accessToken);
  setUserRole(result.role);
  return result;
};

export const logout = async (): Promise<void> => {
  await fetch(`${authBase}/logout`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });
  clearAuth();
};

export const getAuthHeaders = () => {
  const token = getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};
